/** "Why this week" — the plan explaining itself.
 *
 *  A plan a rep cannot interrogate is one they either follow blindly or ignore, and both
 *  are worse than a plan they can argue with. Everything here comes from the backend that
 *  made the decision; nothing is re-derived in the browser, because a second implementation
 *  of the rules is a second set of rules.
 */
import React from 'react';
import './SalesPlanWhy.css';
import { formatLeg, formatMiles } from './salesPlanFormat';
import type { PlanCapacity, PlannedStop, WeekPlanBlob } from '../../api/types';

interface SalesPlanWhyProps {
  stops: PlannedStop[];
  capacity: PlanCapacity | null;
  plan: WeekPlanBlob | undefined;
  visitTypeHelp: Record<string, string> | undefined;
}

/** Rep-facing names for the score parts. The API keys are not prose. */
const PART_LABEL: Record<string, string> = {
  health: 'Relationship health',
  contact_need: 'Overdue for a touch',
  band_nudge: 'Where you put them on the board',
  actionability: 'Signals a visit could actually move',
  account_size: 'Account size, against your own book',
  target: 'On a leader target list',
  opportunity: 'Opportunity: win their business, grow their share, or momentum',
};

const pct = (n: number) => `${Math.round(n * 100)}%`;

const ScoreBar: React.FC<{ parts: Record<string, number> }> = ({ parts }) => {
  const total = parts.total || 0;
  const shown = Object.keys(PART_LABEL).filter((k) => (parts[k] ?? 0) > 0);
  if (!shown.length) return null;
  return (
    <div className="spw-score">
      <div className="spw-bar" aria-hidden>
        {shown.map((k) => (
          <span
            key={k}
            className={`spw-seg spw-seg-${k}`}
            style={{ flexGrow: parts[k] }}
            title={`${PART_LABEL[k]}: ${parts[k].toFixed(3)}`}
          />
        ))}
      </div>
      <ul className="spw-parts">
        {shown.map((k) => (
          <li key={k}>
            <span className={`spw-dot spw-seg-${k}`} />
            {PART_LABEL[k]}
            <strong>{parts[k].toFixed(3)}</strong>
          </li>
        ))}
        <li className="spw-total">Priority<strong>{total.toFixed(3)}</strong></li>
      </ul>
    </div>
  );
};

export const SalesPlanWhy: React.FC<SalesPlanWhyProps> = ({
  stops, capacity, plan, visitTypeHelp,
}) => {
  const visits = stops.filter((s) => s.contact_mode === 'VISIT');
  const calls = stops.filter((s) => s.contact_mode === 'CALL');
  // Only the types actually in THIS week: a glossary of six when the rep has two is noise.
  const typesInPlay = [...new Set(stops.map((s) => s.visit_type))].sort();

  return (
    <div className="spw-root">
      <section className="spw-block">
        <h4>How this week was built</h4>
        <ol className="spw-steps">
          <li>
            <strong>Who was eligible.</strong> Everyone in the territory, minus anyone still
            resting inside the cadence their last close-out set.
          </li>
          <li>
            <strong>Ranked.</strong> Relationship health and how overdue a touch is, blended
            {capacity ? ` ${pct(capacity.health_weight ?? 0.65)} / `
              : ' '}
            {capacity ? `${pct(1 - (capacity.health_weight ?? 0.65))}` : ''}, then nudged by
            where you put them on the board and by account size, and lifted when a leader has put
            them on a target list or there is an opportunity: winning their business, growing a
            share that trails similar dealers, or momentum. The board&rsquo;s tiers
            are health only; this ranking is the plan&rsquo;s own, and the sort you pick on the
            board does not change it.
          </li>
          <li>
            <strong>Split.</strong> Visit or call, before any routing — sequencing a call
            makes the drive both wrong and longer. A call that is on a leader target list, has
            just started doing business with us, or gives us less than similar dealers do, is
            offered to the route as a visit, and stays a call if no trip reaches it.
          </li>
          <li>
            <strong>Routed.</strong>{' '}
            {capacity?.trip_anchor === 'pipeline'
              ? 'Each trip anchors on a client with live pipeline (a deal off track, a delivery at risk, or a close within 30 days) while one is in reach, then on the top visit'
              : 'The top visit anchors a trip'}; the trip fills with the
            best clients within {capacity ? `${capacity.max_radius_mi} mi` : 'the trip radius'}
            {capacity?.max_drive_min ? ` and ${capacity.max_drive_min} minutes\u2019 rush-hour drive` : ''}
            {capacity?.urgency_pull_mi && capacity.urgency_pull_mi > capacity.max_radius_mi
              ? `, reaching ${capacity.urgency_pull_mi} mi for an URGENT one`
              : ''}, ordered by drive time where it is known. Repeat until the week is full.
          </li>
          <li>
            <strong>Topped up.</strong> Visit seats the priority visits cannot fill go to
            drop-ins: the best remaining clients near the route, including accounts not
            trading with us. A drop-in never takes a seat a priority visit could have.
          </li>
        </ol>
        {plan?.notes?.length ? (
          <ul className="spw-notes">
            {plan.notes.map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        ) : null}
      </section>

      {typesInPlay.length && visitTypeHelp ? (
        <section className="spw-block">
          <h4>What the visit types mean</h4>
          <dl className="spw-glossary">
            {typesInPlay.map((t) => (
              <React.Fragment key={t}>
                <dt>{t}</dt>
                <dd>{visitTypeHelp[t] ?? 'No description available.'}</dd>
              </React.Fragment>
            ))}
          </dl>
        </section>
      ) : null}

      <section className="spw-block">
        <h4>Why each client is here</h4>
        {[['Visits', visits], ['Calls', calls]].map(([label, group]) => {
          const list = group as PlannedStop[];
          if (!list.length) return null;
          return (
            <div key={label as string} className="spw-group">
              <h5>{label as string} · {list.length}</h5>
              {list.map((s) => (
                <details className="spw-client" key={s.client_id}>
                  <summary>
                    <span className="spw-client-name">{s.client_name ?? s.client_id}</span>
                    <span className="spw-client-type">{s.visit_type}</span>
                    <span className="spw-client-score">{s.priority_score.toFixed(3)}</span>
                    {s.reason_for_visit ? (
                      <span className="spw-client-focus">{s.reason_for_visit}</span>
                    ) : null}
                  </summary>
                  <div className="spw-client-body">
                    {s.talking_points?.length ? (
                      <>
                        <strong className="spw-points-title">Talk about</strong>
                        <ul className="spw-points">
                          {s.talking_points.map((p) => <li key={p}>{p}</li>)}
                        </ul>
                      </>
                    ) : null}
                    {s.mode_reason ? <p className="spw-mode">{s.mode_reason}</p> : null}
                    {s.reasons.length ? (
                      <p className="spw-reasons">
                        <strong>What is firing:</strong> {s.reasons.join(' · ')}
                      </p>
                    ) : null}
                    {s.trip_no ? (
                      <p className="spw-route">
                        {s.is_anchor
                          ? `Anchors trip ${s.trip_no} — the week was built around this stop.`
                          : `Trip ${s.trip_no}, stop ${s.stop_no}, `
                            + (s.leg
                              ? `${formatLeg(s).text}${s.leg_from ? '' : ' from the previous stop'}.`
                              : `${formatMiles(s.miles_from_previous)} from the previous stop.`)}
                      </p>
                    ) : null}
                    {s.score_parts ? <ScoreBar parts={s.score_parts} /> : (
                      <p className="spw-stale">
                        This week was frozen before the breakdown existed. It will appear on
                        next week&rsquo;s plan.
                      </p>
                    )}
                  </div>
                </details>
              ))}
            </div>
          );
        })}
      </section>
    </div>
  );
};
